// 登入頁（SPEC AUTH-01）：只有一個登入框——品牌的 VMS client，跟品牌 App 同一個帳號（不分家戶 / 工作帳號）；沒有帳號可以註冊。
// 兩步驟驗證（AUTH-03）：
//   password → (MFA_REQUIRED) code → 進入
//            → (MFA_ENROLL_REQUIRED，identity 要求先設定時) enroll：掃 QR → 輸入第一個碼 → 抄備援碼 → 進入
// 企業 SSO（plan SPEC #vms-web-sso-ui）：email 欄位離開時查網域，是某組織啟用 SSO 的網域 → 密碼欄上方多〔以〈IdP〉登入〕（密碼登入仍在）。
// 有待接受的邀請（接受邀請頁記下的）→ 登入後殼層自動接受（lib/pendingJoin.ts）。
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import {
  finishEnroll,
  login,
  passkeyErrorCode,
  signup,
  passkeyLogin,
  passwordForgot,
  takeLogoutReason,
  loginWithCode,
  SessionError,
  totpConfirmDuringLogin,
  totpSetup,
  ssoBegin,
  ssoDiscover,
  type EnrollResult,
  type SsoDiscovery,
  type TotpSetup,
} from '../../lib/session';
import { RecoveryCodes, TotpQr } from '../../components/TotpQr';
import { passkeySupported } from '../../lib/webauthn';
import { PasswordInput } from '../../components/PasswordInput';
import { noteSsoLoginOrg } from '../../lib/pendingSsoLink';
import { peekPendingJoin } from '../../lib/pendingJoin';

type Step = 'password' | 'signup' | 'forgot' | 'code' | 'enroll' | 'recovery';

export function LoginPage() {
  const { t } = useTranslation();
  const { config } = useApp();
  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [name, setName] = useState('');
  const [pendingInvite] = useState(() => !!peekPendingJoin());
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [enrolled, setEnrolled] = useState<EnrollResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [reason] = useState(() => takeLogoutReason());
  const [sso, setSso] = useState<SsoDiscovery | null>(null);
  const [forgotSent, setForgotSent] = useState(false);

  async function discover() {
    setSso(await ssoDiscover(email));
  }

  async function withSso() {
    if (!sso) return;
    setBusy(true);
    setErr(null);
    try {
      noteSsoLoginOrg(sso.org_id); // SSO 回 no_account 時登入後要提示連結這個組織（kq Q143）
      window.location.assign(await ssoBegin(sso.org_id, 'login', '/'));
    } catch (x) {
      showError(x, 'auth');
      setBusy(false);
    }
  }

  function showError(x: unknown, prefix: 'auth' | 'mfa') {
    const c = x instanceof SessionError || x instanceof KinioError ? x.code : 'default';
    if (c === 'MFA_TOKEN_INVALID') {
      // 5 分鐘內沒完成 → 回到密碼
      setStep('password');
      setCode('');
      setSetup(null);
    }
    setErr(t(`${prefix}.err_${c}`, { code: c, defaultValue: t(`${prefix}.err_default`, { code: c }) }));
  }

  async function run(fn: () => Promise<void>, prefix: 'auth' | 'mfa') {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (x) {
      showError(x, prefix);
    } finally {
      setBusy(false);
    }
  }

  function submitPassword(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      try {
        await login(email, password);
      } catch (x) {
        if (x instanceof SessionError && x.code === 'MFA_REQUIRED') {
          setPassword('');
          setStep('code');
          return;
        }
        if (x instanceof SessionError && x.code === 'MFA_ENROLL_REQUIRED') {
          setPassword('');
          setSetup(await totpSetup());
          setStep('enroll');
          return;
        }
        throw x;
      }
    }, 'auth');
  }

  function submitForgot(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await passwordForgot(email);
      setForgotSent(true);
    }, 'auth');
  }

  function toForgot(on: boolean) {
    setErr(null);
    setForgotSent(false);
    setStep(on ? 'forgot' : 'password');
  }

  async function withPasskey() {
    setBusy(true);
    setErr(null);
    try {
      await passkeyLogin();
    } catch (x) {
      const c = passkeyErrorCode(x);
      setErr(t(`passkey.err_${c}`, { code: c, defaultValue: t('passkey.err_default', { code: c }) }));
    } finally {
      setBusy(false);
    }
  }

  function submitSignup(e: FormEvent) {
    e.preventDefault();
    if (password !== password2) {
      setErr(t('activate.mismatch'));
      return;
    }
    void run(async () => {
      await signup(email, password, name);
    }, 'auth');
  }

  function toSignup(on: boolean) {
    setStep(on ? 'signup' : 'password');
    setPassword('');
    setPassword2('');
    setErr(null);
  }

  function submitCode(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await loginWithCode(code);
    }, 'mfa');
  }

  function submitEnroll(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      const r = await totpConfirmDuringLogin(code);
      setEnrolled(r);
      setStep('recovery');
    }, 'mfa');
  }

  function back() {
    setStep('password');
    setCode('');
    setSetup(null);
    setErr(null);
  }

  const errorBox = err && (
    <div className="error" role="alert">
      {err}
    </div>
  );

  return (
    <main className="login">
      <div className="card login-card" aria-labelledby="login-title">
        <div className="login-brand">
          {config.theme.logo && <img src={config.theme.logo} alt="" />}
          <span id="login-title">{config.display_name}</span>
        </div>

        {step === 'password' && (
          <form className="form" onSubmit={submitPassword}>
            {reason && <p className="notice">{t(`auth.reason_${reason}`, { defaultValue: t('auth.reason_other') })}</p>}
            {pendingInvite && <p className="notice">{t('auth.pending_invite')}</p>}
            <p className="muted" style={{ margin: 0 }}>
              {t('auth.hint', { brand: config.display_name })}
            </p>
            <label className="field">
              <span>{t('auth.email')}</span>
              <input
                className="input"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => void discover()}
                data-testid="login-email"
              />
            </label>
            {sso && (
              <div className="sso-login" data-testid="sso-login">
                <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void withSso()}>
                  {t('sso.sign_in_with', { idp: sso.idp_display_name })}
                </button>
                <p className="small muted" style={{ margin: 0 }}>
                  {t('sso.sign_in_hint', { org: sso.org_display_name })}
                </p>
              </div>
            )}
            <label className="field">
              <span>{t('auth.password')}</span>
              <PasswordInput autoComplete="current-password" required={!sso} value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            {errorBox}
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? t('auth.submitting') : t('auth.submit')}
            </button>
            {passkeySupported() && (
              <>
                <div className="divider">
                  <span>{t('passkey.or')}</span>
                </div>
                <button className="btn" type="button" disabled={busy} onClick={() => void withPasskey()}>
                  {t('passkey.sign_in')}
                </button>
              </>
            )}
            <p className="small muted" style={{ margin: 0 }}>
              <button type="button" className="link-btn" onClick={() => toForgot(true)}>
                {t('auth.forgot')}
              </button>
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              {t('auth.no_account')}{' '}
              <button type="button" className="link-btn" onClick={() => toSignup(true)}>
                {t('auth.signup')}
              </button>
            </p>
          </form>
        )}

        {step === 'forgot' && (
          <form className="form" onSubmit={submitForgot}>
            <h2 className="section-title">{t('auth.forgot_title')}</h2>
            {forgotSent ? (
              <p className="notice" role="status">
                {t('auth.forgot_sent')}
              </p>
            ) : (
              <>
                <p className="muted" style={{ margin: 0 }}>
                  {t('auth.forgot_hint')}
                </p>
                <label className="field">
                  <span>{t('auth.email')}</span>
                  <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </label>
                {err && <p className="err">{err}</p>}
                <button className="btn btn-primary" type="submit" disabled={busy}>
                  {t('auth.forgot_submit')}
                </button>
              </>
            )}
            <button type="button" className="link-btn" onClick={() => toForgot(false)}>
              {t('auth.forgot_back')}
            </button>
          </form>
        )}

        {step === 'signup' && (
          <form className="form" onSubmit={submitSignup}>
            <h2 className="section-title">{t('auth.signup_title')}</h2>
            <p className="muted" style={{ margin: 0 }}>
              {t('auth.signup_hint', { brand: config.display_name })}
            </p>
            <label className="field">
              <span>{t('auth.email')}</span>
              <input className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="field">
              <span>{t('activate.display_name')}</span>
              <input className="input" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="field">
              <span>{t('auth.password')}</span>
              <PasswordInput autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
              <span className="small muted">{t('activate.password_hint')}</span>
            </label>
            <label className="field">
              <span>{t('activate.password_again')}</span>
              <PasswordInput autoComplete="new-password" required value={password2} onChange={(e) => setPassword2(e.target.value)} />
            </label>
            <span className="error small" role="status" data-pw-mismatch>
              {password2 && password !== password2 ? t('activate.mismatch') : ''}
            </span>
            {errorBox}
            <div className="row-end">
              <button type="button" className="btn" onClick={() => toSignup(false)}>
                {t('common.back')}
              </button>
              <button className="btn btn-primary" type="submit" disabled={busy}>
                {t('auth.signup_submit')}
              </button>
            </div>
          </form>
        )}

        {step === 'code' && (
          <form className="form" onSubmit={submitCode}>
            <h2 className="section-title">{t('mfa.title')}</h2>
            <p className="muted">{t('mfa.code_body')}</p>
            <label className="field">
              <span>{t('mfa.code')}</span>
              <input
                className="input mono"
                inputMode="text"
                autoComplete="one-time-code"
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
              />
              <span className="small muted">{t('mfa.recovery_hint')}</span>
            </label>
            {errorBox}
            <div className="row-end">
              <button type="button" className="btn" onClick={back}>
                {t('common.back')}
              </button>
              <button className="btn btn-primary" type="submit" disabled={busy}>
                {t('mfa.verify')}
              </button>
            </div>
          </form>
        )}

        {step === 'enroll' && setup && (
          <form className="form" onSubmit={submitEnroll}>
            <h2 className="section-title">{t('mfa.enroll_title')}</h2>
            <p className="muted">{t('mfa.enroll_body')}</p>
            <TotpQr uri={setup.otpauth_uri} secret={setup.secret} />
            <label className="field">
              <span>{t('mfa.first_code')}</span>
              <input
                className="input mono"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
              />
            </label>
            {errorBox}
            <div className="row-end">
              <button type="button" className="btn" onClick={back}>
                {t('common.back')}
              </button>
              <button className="btn btn-primary" type="submit" disabled={busy}>
                {t('mfa.enable')}
              </button>
            </div>
          </form>
        )}

        {step === 'recovery' && enrolled && (
          <div className="form">
            <h2 className="section-title">{t('mfa.recovery_title')}</h2>
            <RecoveryCodes codes={enrolled.recovery_codes} />
            <button className="btn btn-primary" type="button" onClick={() => finishEnroll(enrolled)}>
              {t('mfa.saved_continue')}
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
